/**
 * Main-thread wrapper around the analysis worker, with a synchronous fallback
 * for environments where module workers are unavailable.
 */

import type { SourceAnalysis } from '../types/system';
import type { AnalysisResponse } from '../workers/analysis.worker';

let worker: Worker | null = null;
let nextId = 1;
let workerBroken = false;

function getWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('../workers/analysis.worker.ts', import.meta.url), { type: 'module' });
    worker.onerror = () => {
      workerBroken = true;
      worker = null;
    };
    return worker;
  } catch {
    workerBroken = true;
    return null;
  }
}

export interface AnalyseResult {
  analysis: SourceAnalysis;
  elapsedMs: number;
  onMainThread: boolean;
}

export function analyseBuffer(
  buffer: AudioBuffer,
  onProgress?: (stage: string) => void,
): Promise<AnalyseResult> {
  // Copy channel data: transferring would detach the AudioBuffer's storage in
  // engines that expose it directly, and the buffer is still being played.
  const channels: ArrayBuffer[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const copy = new Float32Array(buffer.length);
    buffer.copyFromChannel(copy, c);
    channels.push(copy.buffer);
  }

  const w = getWorker();
  if (!w) {
    return import('./loudness').then(({ analyse }) => {
      const started = performance.now();
      const analysis = analyse({
        channels: channels.map((b) => new Float32Array(b)),
        sampleRate: buffer.sampleRate,
      });
      return { analysis, elapsedMs: performance.now() - started, onMainThread: true };
    });
  }

  const id = nextId++;
  return new Promise<AnalyseResult>((resolve, reject) => {
    const timeout = setTimeout(() => {
      w.removeEventListener('message', onMessage);
      reject(new Error('Source analysis timed out after 120 seconds.'));
    }, 120000);

    const onMessage = (e: MessageEvent<AnalysisResponse>) => {
      const m = e.data;
      if (!m || m.id !== id) return;
      if (m.type === 'progress') {
        onProgress?.(m.stage);
        return;
      }
      clearTimeout(timeout);
      w.removeEventListener('message', onMessage);
      if (m.type === 'error') reject(new Error(m.message));
      else resolve({ analysis: m.analysis, elapsedMs: m.elapsedMs, onMainThread: false });
    };

    w.addEventListener('message', onMessage);
    w.postMessage({ type: 'analyse', id, channels, sampleRate: buffer.sampleRate }, channels);
  });
}
